import React, { useState, useMemo } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Button } from '../ui/button';

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOD_LABELS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0') + 'h');

// Hex values used only in computed style props (color interpolation)
const CELL_COLORS = { low: '#1d2230', high: '#d4a843' };

function fmtEur(n) {
  if (!n) return '€0';
  if (n >= 1000000) return '€' + (n / 1000000).toFixed(2) + 'M';
  if (n >= 1000) return '€' + (n / 1000).toFixed(1) + 'K';
  return '€' + Math.round(n).toLocaleString('en-GB');
}

function lerp(a, b, t) { return a + (b - a) * t; }

function hexToRgb(hex) {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function cellColor(normalised) {
  if (normalised <= 0) return CELL_COLORS.low;
  const [r1, g1, b1] = hexToRgb(CELL_COLORS.low);
  const [r2, g2, b2] = hexToRgb(CELL_COLORS.high);
  const t = Math.pow(normalised, 0.6);
  return `rgb(${Math.round(lerp(r1, r2, t))},${Math.round(lerp(g1, g2, t))},${Math.round(lerp(b1, b2, t))})`;
}

function BarStrip({ label, bars, formatTick, tickEvery = 1 }) {
  const maxVal = Math.max(...bars.map(b => b.value), 1);
  return (
    <div>
      <p className="text-[10px] font-bold text-ink-dim uppercase tracking-wide mb-1.5">{label}</p>
      <div className="flex items-end gap-px" style={{ height: 52 }}>
        {bars.map((b, i) => {
          const pct = b.value / maxVal;
          return (
            <Tooltip key={i}>
              <TooltipTrigger asChild>
                <div className="flex-1 flex flex-col items-center justify-end gap-px cursor-default group">
                  <div
                    className="w-full rounded-t-sm transition-opacity group-hover:opacity-75"
                    style={{
                      height: Math.max(pct * 40, b.value > 0 ? 2 : 0),
                      backgroundColor: CELL_COLORS.high,
                      opacity: 0.3 + pct * 0.7,
                    }}
                  />
                  <span
                    className="text-[8px] text-ink-dim text-center block overflow-hidden"
                    style={{ height: 10, lineHeight: '10px', flexShrink: 0 }}
                  >
                    {tickEvery === 1 || i % tickEvery === 0 ? formatTick(b) : ''}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                <p className="font-bold">{b.fullLabel}</p>
                <p>{b.pctLabel}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}

export default function HeatmapGrid({ cells, sparse }) {
  const [metric, setMetric] = useState('bets');

  const lookup = useMemo(() => {
    const m = {};
    (cells || []).forEach(c => { m[`${c.dow}_${c.hod}`] = c; });
    return m;
  }, [cells]);

  const values = (cells || []).map(c => metric === 'bets' ? c.bet_count : c.stake_eur);
  const max = values.length > 0 ? Math.max(...values) : 1;

  const { dowTotals, hodTotals, grandTotal } = useMemo(() => {
    const dowT = Array(7).fill(0);
    const hodT = Array(24).fill(0);
    let grand = 0;
    (cells || []).forEach(c => {
      const v = metric === 'bets' ? c.bet_count : c.stake_eur;
      dowT[c.dow] += v;
      hodT[c.hod] += v;
      grand += v;
    });
    return { dowTotals: dowT, hodTotals: hodT, grandTotal: grand };
  }, [cells, metric]);

  const dowBars = DOW_LABELS.map((label, i) => {
    const pct = grandTotal > 0 ? (dowTotals[i] / grandTotal) * 100 : 0;
    return {
      value: dowTotals[i],
      fullLabel: label,
      pctLabel: pct.toFixed(1) + '%' + (metric === 'stake' ? ` · ${fmtEur(dowTotals[i])}` : ` · ${dowTotals[i].toLocaleString()} bets`),
      shortLabel: label.slice(0, 2),
      pct,
    };
  });

  const hodBars = HOD_LABELS.map((label, i) => {
    const pct = grandTotal > 0 ? (hodTotals[i] / grandTotal) * 100 : 0;
    return {
      value: hodTotals[i],
      fullLabel: label,
      pctLabel: pct.toFixed(1) + '%' + (metric === 'stake' ? ` · ${fmtEur(hodTotals[i])}` : ` · ${hodTotals[i].toLocaleString()} bets`),
      shortLabel: i % 3 === 0 ? label : '',
      pct,
    };
  });

  return (
    <div className="rounded-lg border border-white/10 bg-surface-1 p-4 flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-ink">Play schedule</p>
          <p className="text-[11px] text-ink-dim mt-0.5">
            {sparse
              ? 'Sparse — player touched fewer than 10 time slots.'
              : 'Heat by day of week × hour of day (UTC).'}
          </p>
        </div>
        <div className="flex gap-1">
          {['bets', 'stake'].map(m => (
            <Button
              key={m}
              variant="ghost"
              size="sm"
              onClick={() => setMetric(m)}
              className={`text-[11px] px-2.5 py-1 h-auto ${
                metric === m ? 'bg-primary-500/20 text-primary-500' : 'text-ink-dim'
              }`}
            >
              {m === 'bets' ? 'Bet count' : 'Stake €'}
            </Button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div style={{ minWidth: 560 }}>
          <div className="flex mb-1">
            <div style={{ width: 36 }} />
            {HOD_LABELS.map((h, i) => (
              <div key={i} className="flex-1 text-center text-[9px] text-ink-dim" style={{ minWidth: 20 }}>
                {i % 3 === 0 ? h : ''}
              </div>
            ))}
          </div>

          {DOW_LABELS.map((day, dow) => (
            <div key={dow} className="flex mb-0.5 items-center">
              <div className="text-[10px] text-ink-dim text-right pr-2 shrink-0" style={{ width: 36 }}>
                {day}
              </div>
              {Array.from({ length: 24 }, (_, hod) => {
                const cell = lookup[`${dow}_${hod}`];
                const value = cell ? (metric === 'bets' ? cell.bet_count : cell.stake_eur) : 0;
                const norm = max > 0 ? value / max : 0;
                return (
                  <Tooltip key={hod}>
                    <TooltipTrigger asChild>
                      <div
                        className="flex-1 rounded-sm cursor-default transition-opacity hover:opacity-80"
                        style={{ minWidth: 20, height: 20, backgroundColor: cellColor(norm), margin: '0 1px' }}
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      <p className="font-bold">{day} {String(hod).padStart(2, '0')}:00 UTC</p>
                      {cell ? (
                        <>
                          <p>{cell.bet_count.toLocaleString()} bets · {fmtEur(cell.stake_eur)}</p>
                          <p className="text-ink-dim">{cell.active_days_in_cell} active day{cell.active_days_in_cell !== 1 ? 's' : ''}</p>
                        </>
                      ) : (
                        <p className="text-ink-dim">No activity</p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          ))}

          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] text-ink-dim">Low</span>
            <div className="rounded" style={{ height: 5, width: 100, background: `linear-gradient(to right, ${CELL_COLORS.low}, ${CELL_COLORS.high})` }} />
            <span className="text-[10px] text-ink-dim">High</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-6 gap-y-4 pt-2 border-t border-white/10">
        <div style={{ minWidth: 200 }}>
          <BarStrip label="% by day of week" bars={dowBars} formatTick={b => b.shortLabel} />
        </div>
        <div className="overflow-x-auto">
          <div style={{ minWidth: 360 }}>
            <BarStrip label="% by hour (UTC)" bars={hodBars} formatTick={b => b.shortLabel} tickEvery={3} />
          </div>
        </div>
      </div>
    </div>
  );
}
